/**
 * The two `File`-shaped adapter seams between the shared file-transfer engine and each surface's
 * platform adapter (extension / web / CLI). Both used to be documented ONLY in prose + enforced by
 * `as unknown as File` double-casts inside the CLI adapters — the exact shape that shipped #824
 * (a caller wrapped the CLI's finalize() placeholder in `new File([...])`, silently destroying the
 * fields `nodeDownloads` reads, and `reflecto receive` stopped saving anything).
 *
 * Both types below are STRUCTURAL SUBSETS/UNIONS chosen to be the smallest honest description of
 * what actually crosses each seam:
 *
 *  - [EngineFileLike] — every member `sendFiles()` reads off an input file. A real DOM `File`
 *    satisfies it, so every browser call site is unchanged; the CLI's stream-backed shim can now
 *    implement it directly instead of pretending to be a full `File`.
 *  - [FinalizedFile] — what a receiver output's `finalize()` may hand back: either a real `File`
 *    (browser/OPFS adapters) or a [FinalizedFileCarrier] (the CLI's path-carrying placeholder).
 *    The engine's own re-wrap step branches on exactly this union.
 *
 * `packages/client-core/src/testing/ftAdapterConformance.ts` is the executable pin for both — each
 * surface runs it against its REAL adapters, so a drift here fails a test instead of a user's save.
 */

/**
 * The EXACT subset of `File` the sender engine (`sendFile.ts`'s `sendFiles`) reads. Enumerated
 * from the engine, not guessed:
 *
 *  - `size` — part-count/chunk-count math (`sizes = files.map((f) => f.size)`), before any byte
 *    is touched, and the manifest `FileEntry.size`.
 *  - `stream()` — PASS 1's single read (`file.stream().getReader()`) for the streaming SHA-256 +
 *    write-through copy. The ONLY byte-producing member.
 *  - `name` / `type` — the manifest `FileEntry`'s `name` / `mime` (`file.type ||
 *    "application/octet-stream"`).
 *
 * NOT read, and therefore deliberately absent: `slice()`, `arrayBuffer()`, `text()`, `bytes()`,
 * `lastModified`, `webkitRelativePath`. A Node-side shim only has to produce bytes lazily; forcing
 * it to be a whole `Blob` would mean buffering the entire file in RAM (`node:buffer`'s `File`
 * takes `BlobPart[]`), which the flat-memory acceptance criterion forbids.
 */
export interface EngineFileLike {
  readonly name: string;
  readonly size: number;
  readonly type: string;
  stream(): ReadableStream<Uint8Array<ArrayBuffer>>;
}

/**
 * [EngineFileLike]'s member names as data — the runtime half of the same enumeration. Used by the
 * adapter conformance suite to assert each surface's real input value exposes every one of them;
 * `fileSeam.test.ts` pins this list against `keyof EngineFileLike`, so adding an interface member
 * without adding it here fails to compile.
 */
export const ENGINE_FILE_MEMBERS = ["name", "size", "type", "stream"] as const;

/**
 * The non-`Blob` value a receiver output's `finalize()` is allowed to return: a lightweight
 * handle whose two `reflecto*Path` fields tell the SAME surface's downloads adapter where the
 * already-durably-written bytes live. Used by the CLI (`nodeReceiverOutput.ts` ->
 * `nodeDownloads.download`), whose "save" is an atomic `rename(tempPath, outputPath)` — never a
 * byte copy. Re-buffering a multi-hundred-MiB file into a real `File` just to satisfy a type is
 * exactly what the flat-memory criterion forbids.
 *
 * LOAD-BEARING FIELDS: `reflectoTempPath` + `reflectoOutputPath`. They are the whole reason the
 * carrier exists, and the only fields any consumer reads — [isFinalizedFileCarrier] tests exactly
 * these two. `name`/`size`/`type` are an inert `File`-shaped façade retained so the value still
 * reads as file-ish in a debugger; nothing branches on them.
 *
 * A per-transfer carrier (rather than closure state on the downloads object) is required because
 * `ReceiveResumeDeps.downloads` is ONE object shared by every concurrent transfer — the routing
 * has to ride on the `finalize()` value.
 */
export interface FinalizedFileCarrier {
  readonly name: string;
  readonly size: number;
  readonly type: string;
  /** Where the verified bytes are staged right now (`outputPath + ".reflecto-part"`). */
  readonly reflectoTempPath: string;
  /** Where they must end up — pinned at accept time, never re-derived from wire data. */
  readonly reflectoOutputPath: string;
}

/**
 * What crosses the finalize -> downloads handoff. Browser adapters (OPFS-backed) return a real
 * `File`; the CLI returns a [FinalizedFileCarrier]. The engine's receive tails
 * (`receiveHost.ts` / `receiveResume.ts`) branch on `instanceof Blob` and re-wrap ONLY the real
 * one with the manifest's true name+MIME (#824's `.txt` bug); a carrier passes through untouched.
 */
export type FinalizedFile = File | FinalizedFileCarrier;

/**
 * The engine's finalize -> downloads re-wrap step, extracted verbatim from the identical tails of
 * `receiveHost.ts` and `receiveResume.ts` so BOTH share one implementation — and so the adapter
 * conformance suite can exercise the REAL step rather than a copy of it. This function is where
 * #824 lived: it used to `new File([finished], ...)` unconditionally, which destroyed the CLI
 * carrier's paths and silently stopped every `reflecto receive` from saving.
 *
 * Re-wrap ONLY a real `Blob`: browser/OPFS adapters hand back a `File` whose `type` is `""`, and
 * that empty MIME is what made Chrome's safe-filename pass rewrite every saved extension to
 * `.txt`, so those need the manifest's name+mime applied. `new File([blob], ...)` references the
 * same bytes — no copy. A [FinalizedFileCarrier] passes through untouched.
 *
 * `entry.mime` is E2E manifest content; it never leaves the client.
 */
export function rewrapFinalizedFile(
  finished: FinalizedFile,
  entry: { name: string; mime: string }
): FinalizedFile {
  if (!(finished instanceof Blob)) return finished;
  return new File([finished], entry.name, { type: entry.mime || "application/octet-stream" });
}

/**
 * Narrow a [FinalizedFile] to the CLI-style path carrier. Tests exactly the two load-bearing
 * path fields — byte-identical to the hand-written `!ph.reflectoTempPath || !ph.reflectoOutputPath`
 * check this replaced, so no previously-accepted value is now rejected.
 */
export function isFinalizedFileCarrier(value: unknown): value is FinalizedFileCarrier {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<FinalizedFileCarrier>;
  return (
    typeof candidate.reflectoTempPath === "string" &&
    candidate.reflectoTempPath.length > 0 &&
    typeof candidate.reflectoOutputPath === "string" &&
    candidate.reflectoOutputPath.length > 0
  );
}
